import React, { useEffect, useState } from "react";
import Select from "react-select";
import { getDashboardData, getEntities, getStoresForDashboard, createDashboardShare, createPicturesShare, getManufacturerPresets } from "../API";
import { PROVINCES } from "../Constants";
import DropdownMultiSelect from "../components/DropdownMultiSelect";
import { Icon } from "../util/Icon";
import DashboardCharts from "./DashboardCharts";
import DashboardMerchandising from "./DashboardMerchandising";

function normalizeResponse(response) {
  if (Array.isArray(response)) return response;
  if (response?._embedded) return Object.values(response._embedded).flat();
  if (response?.content) return response.content;
  return [];
}

function generateMonths(count) {
  const months = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("en", { month: "long", year: "numeric" });
    months.push({ value, label });
  }
  return months;
}

const MONTH_OPTIONS = generateMonths(24);

const PRODUCT_STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "ALL", label: "All" },
];

export default function Dashboard() {
  const [manufacturers, setManufacturers] = useState([]);
  const [manufacturer, setManufacturer] = useState(null);
  const [presets, setPresets] = useState([]);
  const [showMerchandising, setShowMerchandising] = useState(false);
  const [showStoreFilter, setShowStoreFilter] = useState(false);

  const [selectedMonths, setSelectedMonths] = useState(MONTH_OPTIONS.slice(0, 3));
  const [selectedProvinces, setSelectedProvinces] = useState([]);
  const [productStatus, setProductStatus] = useState(PRODUCT_STATUS_OPTIONS[0]);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [selectedBanners, setSelectedBanners] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [picturesLoading, setPicturesLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      getEntities("manufacturers", "?search=status:ACTIVE&size=1000"),
      getEntities("banners", "?size=1000"),
      getManufacturerPresets(),
    ]).then(([mfrs, bns, prs]) => {
      setManufacturers(normalizeResponse(mfrs).sort((a, b) => a.name.localeCompare(b.name)));
      setBanners(normalizeResponse(bns));
      setPresets(prs);
    });
  }, []);

  useEffect(() => {
    if (!manufacturer) {
      setProducts([]);
      setSelectedProducts([]);
      setSelectedProvinces([]);
      setSelectedBanners([]);
      setStores([]);
      setSelectedStores([]);
      setShowStoreFilter(false);
      setShowMerchandising(false);
      return;
    }
    const preset = presets.find((p) => p.manufacturerId === manufacturer.id);
    setShowMerchandising(preset?.showMerchandising ?? true);
    const presetProvinceIds = preset?.provinceIds ? preset.provinceIds.split(",").map(Number) : [];
    const presetBannerIds = preset?.bannerIds ? preset.bannerIds.split(",").map(Number) : [];
    const presetProductIds = preset?.productIds ? preset.productIds.split(",").map(Number) : [];

    setSelectedProvinces(PROVINCES.filter((p) => presetProvinceIds.includes(p.id)));
    setSelectedBanners(banners.filter((b) => presetBannerIds.includes(b.id)));

    const statusSearch = productStatus.value === "ALL" ? "" : `,status:${productStatus.value}`;
    getEntities("products", `?search=manufacturer:${manufacturer.id}${statusSearch}&size=1000`).then((res) => {
      const list = normalizeResponse(res);
      setProducts(list);
      setSelectedProducts(list.filter((p) => presetProductIds.includes(p.id)));
    });
  }, [manufacturer, productStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showStoreFilter) return;
    const bannerIds = selectedBanners.map((b) => b.id);
    getStoresForDashboard(bannerIds).then((list) => {
      setStores(list);
      const availableIds = new Set(list.map((s) => s.id));
      setSelectedStores((prev) => prev.filter((s) => availableIds.has(s.id)));
    });
  }, [showStoreFilter, selectedBanners]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!manufacturer || selectedMonths.length === 0) return;
    setShareUrl(null);
    setCopied(false);
    const timer = setTimeout(() => {
      setLoading(true);
      setData(null);
      setError(null);
      const allBannersSelected = banners.length > 0 && selectedBanners.length === banners.length;
      const allProductsSelected = products.length > 0 && selectedProducts.length === products.length;
      getDashboardData({
        manufacturerId: manufacturer.id,
        months: selectedMonths.map((m) => m.value),
        provinceIds: selectedProvinces.map((p) => p.id),
        productIds: allProductsSelected ? [] : selectedProducts.map((p) => p.id),
        bannerIds: allBannersSelected ? [] : selectedBanners.map((b) => b.id),
        storeIds: selectedStores.map((s) => s.id),
      })
        .then(setData)
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }, 500);
    return () => clearTimeout(timer);
  }, [manufacturer, selectedMonths, selectedProvinces, selectedProducts, selectedBanners, selectedStores]);

  const handleOpenPictures = async () => {
    setPicturesLoading(true);
    try {
      const sortedMonths = [...selectedMonths].map((m) => m.value).sort();
      const fromDate = sortedMonths[0] + "-01";
      const lastMonth = sortedMonths[sortedMonths.length - 1];
      const [year, month] = lastMonth.split("-").map(Number);
      const toDate = `${lastMonth}-${String(new Date(year, month, 0).getDate()).padStart(2, "0")}`;
      const { token } = await createPicturesShare({
        manufacturerId: manufacturer.id,
        fromDate,
        toDate,
        bannerIds: selectedBanners.map((b) => b.id),
        provinceIds: selectedProvinces.map((p) => p.id),
        territoryIds: [],
        representativeIds: [],
      });
      window.open(`${window.location.origin}/shared/photos/${token}`, "_blank");
    } catch (e) {
      setError(e.message);
    } finally {
      setPicturesLoading(false);
    }
  };

  const handleShare = async () => {
    setSharing(true);
    const allBannersSelected = banners.length > 0 && selectedBanners.length === banners.length;
    const allProductsSelected = products.length > 0 && selectedProducts.length === products.length;
    try {
      const { token } = await createDashboardShare({
        manufacturerId: manufacturer.id,
        manufacturerName: manufacturer.name,
        months: selectedMonths.map((m) => m.value),
        provinceIds: selectedProvinces.map((p) => p.id),
        productIds: allProductsSelected ? [] : selectedProducts.map((p) => p.id),
        bannerIds: allBannersSelected ? [] : selectedBanners.map((b) => b.id),
        storeIds: selectedStores.map((s) => s.id),
        storeNames: selectedStores.map((s) => s.name),
      });
      setShareUrl(`${window.location.origin}/shared/dashboard/${token}`);
      setCopied(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSharing(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
  };

  return (
    <div className="flex-grow-1 overflow-auto p-4">
      <h5 className="mb-3">Distribution Dashboard</h5>

      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-2">
              <label className="form-label small fw-semibold mb-1">Manufacturer</label>
              <Select
                options={manufacturers}
                getOptionLabel={(o) => o.name}
                getOptionValue={(o) => o.id}
                value={manufacturer}
                onChange={(v) => setManufacturer(v ?? null)}
                placeholder="Select manufacturer..."
                isClearable
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-semibold mb-1">Months</label>
              <DropdownMultiSelect
                options={MONTH_OPTIONS}
                value={selectedMonths}
                onChange={setSelectedMonths}
                getOptionLabel={(o) => o.label}
                getOptionValue={(o) => o.value}
                placeholder="Select months…"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-semibold mb-1">Provinces</label>
              <DropdownMultiSelect
                options={PROVINCES}
                value={selectedProvinces}
                onChange={setSelectedProvinces}
                getOptionLabel={(o) => o.name}
                getOptionValue={(o) => o.id}
                placeholder="All provinces"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-semibold mb-1">Product Status</label>
              <Select
                options={PRODUCT_STATUS_OPTIONS}
                value={productStatus}
                onChange={(v) => { setSelectedProducts([]); setProductStatus(v); }}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-semibold mb-1">Products</label>
              <DropdownMultiSelect
                options={products}
                value={selectedProducts}
                onChange={setSelectedProducts}
                getOptionLabel={(o) => [o.description, o.formatQut, o.format].filter(Boolean).join(" ")}
                getOptionValue={(o) => o.id}
                placeholder="All products"
                disabled={!manufacturer}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-semibold mb-1">Banners</label>
              <DropdownMultiSelect
                options={banners}
                value={selectedBanners}
                onChange={setSelectedBanners}
                getOptionLabel={(o) => o.name}
                getOptionValue={(o) => o.id}
                placeholder="All banners"
              />
            </div>
            {loading && (
              <div className="col-md-2 d-flex align-items-end">
                <span className="text-muted small">Loading…</span>
              </div>
            )}
            {data && !loading && (
              <div className="col-md-auto d-flex align-items-end gap-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleOpenPictures}
                  disabled={picturesLoading}
                  title="Open pictures report for these parameters"
                >
                  <Icon iconName="CardImage" className="me-1" />
                  {picturesLoading ? "Opening…" : "Pictures"}
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleShare}
                  disabled={sharing}
                  title="Generate shareable link"
                >
                  <Icon iconName="Share" className="me-1" />
                  {sharing ? "Generating…" : "Share"}
                </button>
              </div>
            )}
          </div>

          <div className="mt-2">
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-muted text-decoration-none"
              style={{ fontSize: "0.75rem" }}
              onClick={() => setShowStoreFilter((v) => !v)}
              disabled={!manufacturer}
            >
              {showStoreFilter ? "▲" : "▼"} Store filter
              {selectedStores.length > 0 && (
                <span className="ms-1 badge bg-secondary" style={{ fontWeight: "normal" }}>
                  {selectedStores.length}
                </span>
              )}
            </button>
          </div>

          {showStoreFilter && (
            <div className="row g-2 mt-1">
              <div className="col-md-6 col-lg-4">
                <DropdownMultiSelect
                  options={stores}
                  value={selectedStores}
                  onChange={setSelectedStores}
                  getOptionLabel={(o) => [
                    o.banner?.name,
                    o.number ? `#${o.number}` : null,
                    [o.civicNo, o.street, o.city].filter(Boolean).join(" "),
                  ].filter(Boolean).join(" – ")}
                  getOptionValue={(o) => o.id}
                  placeholder="All stores"
                />
              </div>
            </div>
          )}

          {shareUrl && (
            <div className="row mt-3">
              <div className="col">
                <div className="input-group">
                  <input type="text" className="form-control form-control-sm" value={shareUrl} readOnly />
                  <button className="btn btn-outline-secondary btn-sm" onClick={handleCopy}>
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {!data && !loading && (
        <p className="text-muted">Select a manufacturer and at least one month to load the dashboard.</p>
      )}

      {data && <DashboardCharts data={data} />}
      {data && showMerchandising && (
        <DashboardMerchandising data={data.merchandising} />
      )}
    </div>
  );
}
